import { useState, useEffect } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export default function InstallPWA() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showInstall, setShowInstall] = useState(false);

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setShowInstall(true);
    };

    window.addEventListener('beforeinstallprompt', handler);
    
    // Verificar se já está instalado
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setShowInstall(false);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    
    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('PWA instalado com sucesso');
      }
      
      setShowInstall(false);
      setDeferredPrompt(null);
    } catch (error) {
      console.error('Erro ao instalar PWA:', error);
    }
  };

  const handleDismiss = () => {
    setShowInstall(false);
    // Guardar no localStorage para não mostrar novamente nesta sessão
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  // Não mostrar se foi dispensado recentemente (últimas 24 horas)
  useEffect(() => {
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed) {
      const dayInMs = 24 * 60 * 60 * 1000;
      if (Date.now() - parseInt(dismissed) < dayInMs) {
        setShowInstall(false);
      }
    }
  }, []);

  if (!showInstall || !deferredPrompt) return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-gradient-to-r from-pink-500 to-purple-500 text-white p-4 rounded-2xl shadow-2xl z-50 animate-fade-in">
      <button
        onClick={handleDismiss}
        className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center text-white/80 hover:text-white transition-colors"
        aria-label="Fechar"
      >
        <i className="ri-close-line text-lg"></i>
      </button>
      
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
          <i className="ri-download-cloud-line text-2xl"></i>
        </div>
        
        <div className="flex-1">
          <h3 className="font-bold text-base mb-1">Instalar Saboré</h3>
          <p className="text-sm opacity-90 mb-3">
            Adicione à sua tela inicial para acesso rápido e melhor experiência
          </p>
          
          <div className="flex gap-2">
            <button
              onClick={handleInstall}
              className="flex-1 px-4 py-2 bg-white text-pink-600 rounded-xl text-sm font-medium hover:bg-pink-50 transition-colors shadow-md"
            >
              <i className="ri-download-line mr-1"></i>
              Instalar
            </button>
            <button
              onClick={handleDismiss}
              className="px-4 py-2 bg-white/20 rounded-xl text-sm font-medium hover:bg-white/30 transition-colors backdrop-blur-sm"
            >
              Depois
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

